import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import App from './App';
import AdminApp from './admin/AdminApp';
import MarketingLandingPage from './pages/MarketingLandingPage';
import BetaAccessPage from './pages/BetaAccessPage';
import StreamPreviewPage from './pages/StreamPreviewPage';
import SpadeslaunchPage from './pages/SpadeslaunchPage';
import GetAppPage from './pages/GetAppPage';
import GetAppCard from './components/GetAppCard';
import ResetPasswordPage from './pages/ResetPasswordPage';
import TournamentPage from './pages/TournamentPage';
import PrivacyPolicy from './legal/PrivacyPolicy';
import TermsOfService from './legal/TermsOfService';
import CommunityGuidelines from './legal/CommunityGuidelines';
import { Toaster } from './components/ui/sonner';

/**
 * Root application router.
 *
 * All routing is path-based under a single domain:
 *   /              → Marketing landing page
 *   /app/*         → Main web application (beta gated)
 *   /admin/*       → Admin portal
 *   /beta-access   → Beta access page (login/code/waitlist)
 *   /watch/:id     → Shared livestream preview (public, no beta gate)
 *   /privacy       → Privacy policy
 *   /terms         → Terms of service
 */
function AppRoot() {
  const pathname = window.location.pathname;
  const search = window.location.search;
  
  console.log('🔍 AppRoot routing:', { pathname, search });

  // Admin portal: /admin/*
  if (pathname.startsWith('/admin')) {
    return <AdminApp basename="/admin" />;
  }

  // Main web app: /app/*
  if (pathname.startsWith('/app')) {
    console.log('🔍 Routing to App component with search:', search);
    // Round 44: the web app stays, with a small "best on the app" card
    return (
      <>
        <App basename="/app" />
        <GetAppCard />
      </>
    );
  }

  // Round 50: a tournament's own page — gemslive.tv/t/<name> (the short link on flyers and in messages)
  if (pathname.startsWith('/t/') || pathname.startsWith('/tournament/')) {
    return <TournamentPage />;
  }

  // Round 48: gemslive.tv/table — the address GEMS's social posts end with — is the get-the-app page
  if (pathname === '/table' || pathname.startsWith('/table/')) {
    return <GetAppPage />;
  }

  // Round 46: the page the password-reset email links to (it never existed; the link went to the home page)
  if (pathname.startsWith('/reset-password')) {
    return <ResetPasswordPage />;
  }

  // Beta access page: /beta-access
  if (pathname.startsWith('/beta-access')) {
    return <BetaAccessPage />;
  }

  // Shared livestream link: /watch/:streamId
  // This sits OUTSIDE the beta gate — preview first, landing later
  if (pathname.startsWith('/watch/')) {
    return <StreamPreviewPage />;
  }

  // Landing page + legal pages (everything else)
  return (
    <BrowserRouter>
      <Toaster position="top-right" />
      <Routes>
        <Route path="/spadeslaunch" element={<SpadeslaunchPage />} />
        {/* Round 40c: the printed QR / "Get the app" link — public, outside the beta gate */}
        <Route path="/get-app" element={<GetAppPage />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/community-guidelines" element={<CommunityGuidelines />} />
        <Route path="/" element={<MarketingLandingPage />} />
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </BrowserRouter>
  );
}

export default AppRoot;
